import Navigation from "@/components/Navigation";
import Breadcrumbs from "@/components/Breadcrumbs";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Zap, TrendingUp, Shield, Link as LinkIcon, Shirt, Building2, Factory, Database, Package, Truck } from "lucide-react";
import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import RelatedSolutionsSection from "@/components/RelatedSolutionsSection";

const Ecommerce = () => {
  useSEO({
    title: 'E-commerce Operations Platform | Ommni - Unified Commerce Solution',
    description: 'Power your e-commerce business with Ommni. Unified catalog management, real-time order sync, dynamic inventory updates, and marketplace compliance. Integrate with Shopify, Amazon, and more.',
    keywords: 'ecommerce platform, online retail, ecommerce operations, multi-channel selling, online store management',
    canonical: 'https://ommni.io/solutions/ecommerce',
  });

  const features = [
    {
      icon: ShoppingCart,
      title: "Unified Catalog Management",
      description: "Manage product information, variants, and pricing across all channels from a single dashboard.",
    },
    {
      icon: Zap,
      title: "Real-Time Order Sync",
      description: "Automatically sync orders from all platforms and route them to optimal fulfillment centers.",
    },
    {
      icon: TrendingUp,
      title: "Dynamic Inventory Updates",
      description: "Prevent overselling with instant inventory updates across Shopify, Amazon, eBay, and more.",
    },
    {
      icon: Shield,
      title: "Marketplace Compliance",
      description: "Stay compliant with marketplace requirements and automatically update product data.",
    },
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "E-commerce & Retail Operations Platform",
    "description": "Power your multi-channel retail operations with seamless integrations and real-time synchronization. Unified catalog management, order sync, dynamic inventory updates, and marketplace compliance.",
    "serviceType": "E-commerce Operations Platform",
    "provider": {
      "@type": "Organization",
      "name": "Ommni",
      "url": "https://ommni.io"
    },
    "areaServed": {
      "@type": "Place",
      "name": "Global"
    },
    "url": "https://ommni.io/solutions/ecommerce"
  };

  return (
    <div className="min-h-screen">
      <Navigation />
      <main className="pt-32 px-4 pb-24">
        {/* JSON-LD Structured Data for Service */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        />
        
        <div className="container mx-auto max-w-7xl">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary/10 mb-6">
              <ShoppingCart className="h-10 w-10 text-primary" />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              E-commerce & Retail Solutions
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
              Power your multi-channel retail operations with seamless integrations and real-time synchronization
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button variant="cta" size="lg" asChild>
                <Link to="/contact">Get Started</Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/pricing">View Pricing</Link>
              </Button>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {features.map((feature, index) => (
              <Card key={index} className="p-8 gradient-card">
                <div className="mb-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                </div>
                <h3 className="text-2xl font-bold mb-3">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </Card>
            ))}
          </div>

          {/* Powered By Section */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-center mb-4">
              Powered By Ommni Features
            </h2>
            <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
              E-commerce solutions built on our core platform capabilities
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="p-6 hover:shadow-lg transition-all hover:border-primary/50">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Database className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold mb-2">
                  <Link to="/features/pim" className="hover:text-primary transition-colors">
                    Product Information Management
                  </Link>
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Centralize product catalogs and publish to all e-commerce channels instantly.
                </p>
              </Card>
              <Card className="p-6 hover:shadow-lg transition-all hover:border-primary/50">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Package className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold mb-2">
                  <Link to="/features/inventory" className="hover:text-primary transition-colors">
                    Inventory Management
                  </Link>
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Prevent overselling with real-time inventory sync across all marketplaces.
                </p>
              </Card>
              <Card className="p-6 hover:shadow-lg transition-all hover:border-primary/50">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Truck className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-bold mb-2">
                  <Link to="/features/dom" className="hover:text-primary transition-colors">
                    Order Management
                  </Link>
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Route e-commerce orders intelligently for faster, cost-effective fulfillment.
                </p>
              </Card>
            </div>
          </div>

          {/* Related Solutions */}
          <RelatedSolutionsSection
            solutions={[
              {
                icon: Shirt,
                title: "Fashion & Apparel",
                description: "Complex variant management for fashion retailers.",
                link: "/solutions/fashion",
                gradient: "bg-gradient-to-br from-pink-500/20 to-rose-500/20",
              },
              {
                icon: Building2,
                title: "Wholesale & Distribution",
                description: "B2B operations with automated workflows.",
                link: "/solutions/wholesale",
                gradient: "bg-gradient-to-br from-purple-500/20 to-pink-500/20",
              },
              {
                icon: Factory,
                title: "Manufacturing",
                description: "Production to distribution management.",
                link: "/solutions/manufacturing",
                gradient: "bg-gradient-to-br from-orange-500/20 to-red-500/20",
              },
            ]}
          />

          {/* Benefits Section */}
          <div className="relative overflow-hidden rounded-lg border-2 bg-card p-12 text-center">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-purple-500/5 to-background"></div>
            <div className="relative z-10">
              <h2 className="text-3xl font-bold mb-6">
                Supported E-commerce Platforms
              </h2>
              <div className="flex flex-wrap justify-center gap-8 mb-8">
                <div className="flex items-center gap-2">
                  <LinkIcon className="h-5 w-5 text-primary" />
                  <span className="font-semibold">Shopify</span>
                </div>
                <div className="flex items-center gap-2">
                  <LinkIcon className="h-5 w-5 text-primary" />
                  <span className="font-semibold">Amazon</span>
                </div>
                <div className="flex items-center gap-2">
                  <LinkIcon className="h-5 w-5 text-primary" />
                  <span className="font-semibold">eBay</span>
                </div>
                <div className="flex items-center gap-2">
                  <LinkIcon className="h-5 w-5 text-primary" />
                  <span className="font-semibold">WooCommerce</span>
                </div>
                <div className="flex items-center gap-2">
                  <LinkIcon className="h-5 w-5 text-primary" />
                  <span className="font-semibold">Magento</span>
                </div>
                <div className="flex items-center gap-2">
                  <LinkIcon className="h-5 w-5 text-primary" />
                  <span className="font-semibold">BigCommerce</span>
                </div>
              </div>
              <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
                Connect all your sales channels and marketplaces in minutes with pre-built integrations
              </p>
              <Button variant="cta" size="lg" asChild>
                <Link to="/integrations">View All Integrations</Link>
              </Button>
            </div>
          </div>
        </div>
      </main>
      <Breadcrumbs />
      <Footer />
    </div>
  );
};

export default Ecommerce;
